import React, { useEffect, useMemo, useState } from 'react';
import InventoryBarcodeScanner from './InventoryBarcodeScanner';
import InventoryBuildGuide from './InventoryBuildGuide';
import {
  buildInventoryReconciliation,
  getBarcodeMap,
  normalizeInventoryBarcode,
  parseInventoryQuantity,
  rememberBarcode,
} from './inventoryScannerModel';
import {
  gs1PrefillSummary,
  parseInventoryBarcodePayload,
} from './gs1BarcodeModel';
import {
  barcodeMappedCatalogIds,
  getGlobalBarcodeMap,
  isInventoryBarcodeRecord,
  makeInventoryBarcodeMappingRecord,
  resolveBarcodeCatalogId,
} from './inventoryBarcodeLibraryModel';
import {
  formatInventoryExpirationInput,
  normalizeInventoryExpirationInput,
} from './inventoryExpirationInputModel';
import {
  PHYSICAL_INVENTORY_TARGETS,
  activeUnitRecords,
  countInventoryUnits,
  inventoryCatalogForTarget,
  inventoryTargetType,
  inventoryTrackingMode,
  isInventoryModeRecord,
  makeCountedInventoryRecords,
  makeInventoryTrackingModeChangeRecords,
  makeInventoryUnitRecord,
  normalizeInventoryTargetId,
} from './inventoryUnitModel';
import './inventory-scanner.css';
import './inventory-unit.css';

const INVENTORY_SHEET_URL = 'https://docs.google.com/spreadsheets/d/1tuhGI9FVE4UNQxGe6hDT63M5owmxEfZ-aStfpiLZGIo/edit';
const CURRENT_STOCK_GID = '173478742';
const ARCHIVE_GID = '172670107';

function catalogSearchText(item) {
  return [item?.item, item?.component, item?.catalogId, item?.notes]
    .map(value => String(value || '').toLowerCase())
    .join(' ');
}

function normalizeCatalogItemName(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function formatInventoryDate(value) {
  if (!value) return 'N/A';
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value));
  if (!match) return value;
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]), 12, 0, 0, 0);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString();
}

function localInventoryDate() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
}

function uniqueTargets(expirations) {
  const map = new Map(PHYSICAL_INVENTORY_TARGETS.map(item => [item.id, item]));
  (Array.isArray(expirations) ? expirations : []).forEach(record => {
    if (isInventoryModeRecord(record) || isInventoryBarcodeRecord(record)) return;
    const id = normalizeInventoryTargetId(record?.setId);
    if (!id || map.has(id)) return;
    map.set(id, {
      id,
      name: String(record?.setName || id).trim() || id,
      type: 'existing',
      seasonalStatus: 'active',
    });
  });
  return [...map.values()];
}

function newestUnit(records) {
  return [...(records || [])].sort((a, b) => {
    const aTime = Date.parse(a?.updatedAt || '') || 0;
    const bTime = Date.parse(b?.updatedAt || '') || 0;
    return bTime - aTime;
  })[0] || null;
}

function scanPrefill(parsed) {
  if (!parsed?.isGs1 || !gs1PrefillSummary(parsed)) return null;
  const notes = parsed.serial && parsed.lot ? `GS1 serial ${parsed.serial}` : '';
  return {
    expirationDate: formatInventoryExpirationInput(parsed.expirationDate),
    noExpiration: false,
    lotSerial: String(parsed.lot || parsed.serial || ''),
    notes,
  };
}

export default function InventoryUnitScanner() {
  const [data, setData] = useState({ catalog: [], expirations: [], settings: {} });
  const [status, setStatus] = useState('loading');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [targetId, setTargetId] = useState('LM-PACK-01');
  const [targetName, setTargetName] = useState('Line Medic Pack 01');
  const [barcodeMap, setBarcodeMap] = useState({});
  const [currentBarcode, setCurrentBarcode] = useState('');
  const [currentScan, setCurrentScan] = useState(null);
  const [pendingBarcode, setPendingBarcode] = useState('');
  const [pendingScan, setPendingScan] = useState(null);
  const [scanFormPrefill, setScanFormPrefill] = useState(null);
  const [selectedCatalogId, setSelectedCatalogId] = useState('');
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);
  const [barcodeSaving, setBarcodeSaving] = useState(false);
  const [modeSaving, setModeSaving] = useState(false);
  const [countQuantity, setCountQuantity] = useState('');
  const [form, setForm] = useState({ expirationDate: '', noExpiration: false, lotSerial: '', notes: '' });
  const [prefilledSource, setPrefilledSource] = useState('');

  const load = async () => {
    setStatus('loading');
    setError('');
    try {
      const response = await fetch('/api/tracking?admin=inventory', { cache: 'no-store' });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload?.error || 'Unable to load inventory.');
      setData(payload);
      if (typeof window !== 'undefined') setBarcodeMap(getBarcodeMap(window.localStorage));
      setStatus('ready');
    } catch (err) {
      setError(err?.message || 'Unable to load inventory.');
      setStatus('error');
    }
  };

  useEffect(() => { load(); }, []);

  const targets = useMemo(() => uniqueTargets(data.expirations), [data.expirations]);
  const globalBarcodeMap = useMemo(() => getGlobalBarcodeMap(data.expirations), [data.expirations]);
  const targetMeta = useMemo(() => targets.find(item => item.id === normalizeInventoryTargetId(targetId)) || null, [targets, targetId]);
  const target = useMemo(() => ({ id: normalizeInventoryTargetId(targetId), name: targetName.trim() || normalizeInventoryTargetId(targetId) }), [targetId, targetName]);
  const guidedTarget = ['pack', 'bls', 'trauma', 'o2', 'aed'].includes(inventoryTargetType(target.id));
  const scopedCatalog = useMemo(() => inventoryCatalogForTarget(data.catalog, target.id), [data.catalog, target.id]);
  const scopedCatalogIds = useMemo(() => new Set(scopedCatalog.map(item => item.catalogId)), [scopedCatalog]);
  const selectedCatalog = useMemo(() => scopedCatalog.find(item => item.catalogId === selectedCatalogId) || null, [scopedCatalog, selectedCatalogId]);
  const selectedTrackingMode = selectedCatalog ? inventoryTrackingMode(selectedCatalog, data.expirations) : 'unit';
  const selectedRecords = useMemo(
    () => selectedCatalog ? activeUnitRecords(data.expirations, target.id, selectedCatalog.catalogId) : [],
    [data.expirations, target.id, selectedCatalog],
  );
  const selectedCount = countInventoryUnits(selectedRecords);
  const selectedRequired = selectedCatalog ? Math.max(0, parseInventoryQuantity(selectedCatalog.requiredQty, 1)) : 0;
  const warningDays = Math.max(1, Number(data.settings?.expiration_warning_days) || 180);
  const reconciliation = useMemo(
    () => buildInventoryReconciliation(scopedCatalog, data.expirations, target.id, warningDays),
    [scopedCatalog, data.expirations, target.id, warningDays],
  );

  const matchingCatalog = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return scopedCatalog.slice(0, 30);
    return scopedCatalog.filter(item => catalogSearchText(item).includes(q)).slice(0, 50);
  }, [scopedCatalog, search]);

  useEffect(() => {
    if (!selectedCatalog) {
      setForm({ expirationDate: '', noExpiration: false, lotSerial: '', notes: '' });
      setCountQuantity('');
      setPrefilledSource('');
      return;
    }

    if (selectedTrackingMode === 'counted') {
      setForm({ expirationDate: '', noExpiration: true, lotSerial: '', notes: '' });
      setCountQuantity(selectedCount > 0 ? String(selectedCount) : '');
      setPrefilledSource('');
      return;
    }

    if (scanFormPrefill) {
      setCountQuantity('');
      setForm(scanFormPrefill);
      setPrefilledSource('gs1');
      return;
    }

    if (currentScan) {
      setCountQuantity('');
      setForm({ expirationDate: '', noExpiration: false, lotSerial: '', notes: '' });
      setPrefilledSource('');
      return;
    }

    const recent = newestUnit(selectedRecords);
    setCountQuantity('');
    if (!recent) {
      setForm({ expirationDate: '', noExpiration: false, lotSerial: '', notes: '' });
      setPrefilledSource('');
      return;
    }
    setForm({
      expirationDate: formatInventoryExpirationInput(recent.expirationDate),
      noExpiration: String(recent.expirationState || '').toUpperCase() === 'NA',
      lotSerial: String(recent.lotSerial || ''),
      notes: '',
    });
    setPrefilledSource('previous');
  }, [selectedCatalogId, target.id, selectedTrackingMode, selectedCount, scanFormPrefill, currentScan]);

  const clearSelection = () => {
    setSelectedCatalogId('');
    setCurrentBarcode('');
    setCurrentScan(null);
    setPendingBarcode('');
    setPendingScan(null);
    setScanFormPrefill(null);
    setSearch('');
    setCountQuantity('');
    setForm({ expirationDate: '', noExpiration: false, lotSerial: '', notes: '' });
    setPrefilledSource('');
  };

  const chooseTarget = id => {
    const normalized = normalizeInventoryTargetId(id);
    const found = targets.find(item => item.id === normalized);
    setTargetId(normalized);
    setTargetName(found?.name || '');
    clearSelection();
    setMessage('');
    setError('');
  };

  const postRecords = async records => {
    const response = await fetch('/api/tracking?admin=inventory', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ records }),
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(payload?.error || 'Unable to save inventory.');
    setData(payload);
    return payload;
  };

  const rememberLocally = (scan, catalogId) => {
    if (typeof window === 'undefined' || !scan) return;
    let nextMap = getBarcodeMap(window.localStorage);
    scan.lookupKeys.forEach(key => {
      nextMap = rememberBarcode(window.localStorage, key, catalogId);
    });
    const legacyRaw = normalizeInventoryBarcode(scan.raw);
    if (legacyRaw) nextMap = rememberBarcode(window.localStorage, legacyRaw, catalogId);
    setBarcodeMap(nextMap);
  };

  const chooseCatalog = async item => {
    if (!item) return;
    const trackingMode = inventoryTrackingMode(item, data.expirations);
    setSelectedCatalogId(item.catalogId);
    setSearch('');
    if (!pendingScan) {
      setCurrentBarcode('');
      setCurrentScan(null);
      setScanFormPrefill(null);
      return;
    }

    const lookupKey = pendingScan.primaryLookupKey;
    setBarcodeSaving(true);
    setError('');
    try {
      const payload = await postRecords([makeInventoryBarcodeMappingRecord(item, lookupKey)]);
      rememberLocally(pendingScan, item.catalogId);
      setCurrentBarcode(lookupKey);
      setCurrentScan(pendingScan);
      setScanFormPrefill(scanPrefill(pendingScan));
      const gs1Summary = gs1PrefillSummary(pendingScan);
      setMessage(trackingMode === 'counted'
        ? `Barcode mapped globally to ${item.item} for this inventory slot. This is counted / bulk stock, so enter the total physically present.`
        : gs1Summary
          ? `Barcode mapped globally to ${item.item} for this inventory slot. GS1 supplied ${gs1Summary}; verify the prefilled fields before saving.`
          : pendingScan.isGs1
            ? `Barcode mapped globally to ${item.item}. GS1 identified the product but did not include lot or expiration; type them from the package before saving.`
            : `Barcode mapped globally to ${item.item} for this inventory slot. Each save creates one physical unit.`);
      setPendingBarcode('');
      setPendingScan(null);
      setData(payload);
    } catch (err) {
      setError(err?.message || 'Unable to save the global barcode mapping.');
    } finally {
      setBarcodeSaving(false);
    }
  };

  const handleBarcode = async raw => {
    setError('');
    setMessage('');
    const scan = parseInventoryBarcodePayload(raw);
    if (!scan?.primaryLookupKey) return;
    setCurrentScan(scan);

    const legacyRaw = normalizeInventoryBarcode(scan.raw);
    const legacyCatalogId = barcodeMap[legacyRaw] || '';
    const mappedIdsInScope = barcodeMappedCatalogIds(scan.lookupKeys, globalBarcodeMap)
      .filter(id => scopedCatalogIds.has(id));
    const mappedItemsInScope = mappedIdsInScope
      .map(id => scopedCatalog.find(item => item.catalogId === id))
      .filter(Boolean);
    const selectedMatchesKnownProduct = Boolean(
      selectedCatalog
      && mappedItemsInScope.some(item => normalizeCatalogItemName(item.item) === normalizeCatalogItemName(selectedCatalog.item)),
    );
    const selectedNeedsSlotMapping = Boolean(
      selectedMatchesKnownProduct
      && !mappedIdsInScope.includes(selectedCatalog.catalogId),
    );
    const prefill = scanPrefill(scan);

    if (selectedNeedsSlotMapping) {
      setBarcodeSaving(true);
      try {
        const payload = await postRecords([makeInventoryBarcodeMappingRecord(selectedCatalog, scan.primaryLookupKey)]);
        rememberLocally(scan, selectedCatalog.catalogId);
        setCurrentBarcode(scan.primaryLookupKey);
        setPendingBarcode('');
        setPendingScan(null);
        setScanFormPrefill(prefill);
        setSearch('');
        const gs1Summary = gs1PrefillSummary(scan);
        setMessage(gs1Summary
          ? `Recognized ${selectedCatalog.item}. This product is used in more than one pouch, so the scan stayed on ${selectedCatalog.component}. ${gs1Summary} was prefilled — verify it before saving.`
          : scan.isGs1
            ? `Recognized ${selectedCatalog.item}. This product is used in more than one pouch, so the scan stayed on ${selectedCatalog.component}. Lot and expiration were not supplied by this barcode; type them from the package before saving.`
            : `Recognized ${selectedCatalog.item}. This product is used in more than one pouch, so the scan stayed on ${selectedCatalog.component}.`);
        setData(payload);
      } catch (err) {
        setError(err?.message || 'Unable to add this barcode to the selected inventory slot.');
      } finally {
        setBarcodeSaving(false);
      }
      return;
    }

    const mappedCatalogId = resolveBarcodeCatalogId(
      scan.lookupKeys,
      globalBarcodeMap,
      barcodeMap,
      scopedCatalogIds,
      selectedCatalogId,
    ) || (scopedCatalogIds.has(legacyCatalogId) ? legacyCatalogId : '');
    const mapped = scopedCatalog.find(item => item.catalogId === mappedCatalogId);

    if (mapped) {
      setCurrentBarcode(scan.primaryLookupKey);
      setPendingBarcode('');
      setPendingScan(null);
      setScanFormPrefill(prefill);
      setSelectedCatalogId(mapped.catalogId);
      setSearch('');
      const trackingMode = inventoryTrackingMode(mapped, data.expirations);
      const gs1Summary = gs1PrefillSummary(scan);
      setMessage(trackingMode === 'counted'
        ? `Recognized ${mapped.item}. It is counted / bulk stock; enter the total quantity physically present.`
        : gs1Summary
          ? `Recognized ${mapped.item} from GS1 data. ${gs1Summary} was prefilled — verify it before saving.`
          : scan.isGs1
            ? `Recognized ${mapped.item} from GS1 product data. This barcode did not include lot or expiration; type them from the package before saving.`
            : `Recognized ${mapped.item}. This scan will be saved as one physical unit.`);
      return;
    }

    const knownElsewhereIds = barcodeMappedCatalogIds(scan.lookupKeys, globalBarcodeMap);
    const knownElsewhereItems = knownElsewhereIds
      .map(id => data.catalog.find(item => item.catalogId === id))
      .filter(Boolean);
    const knownElsewhereNames = knownElsewhereItems.map(item => item.item).filter(Boolean);
    const sameNamedKnownItems = [...new Set(knownElsewhereNames.map(normalizeCatalogItemName).filter(Boolean))];
    const suggestedSearch = sameNamedKnownItems.length === 1 ? knownElsewhereNames[0] : '';

    setCurrentBarcode(scan.primaryLookupKey);
    setPendingBarcode(scan.primaryLookupKey);
    setPendingScan(scan);
    setScanFormPrefill(prefill);
    setSelectedCatalogId('');
    setSearch(suggestedSearch);
    const gs1Summary = gs1PrefillSummary(scan);
    const knownElsewhere = knownElsewhereNames.length
      ? ` This product is already known as ${[...new Set(knownElsewhereNames)].join(' / ')}; choose the correct pouch slot for ${target.name}.`
      : '';
    const gs1Detail = gs1Summary
      ? ` GS1 also supplied ${gs1Summary}.`
      : scan.isGs1
        ? ' GS1 identified the product only; lot and expiration were not supplied by this barcode.'
        : '';
    const ambiguousDetail = mappedIdsInScope.length > 1
      ? ' This barcode belongs to multiple slots in this same container, so the app will not choose one automatically.'
      : '';
    setMessage(`Choose inventory slot for ${scan.primaryLookupKey}.${knownElsewhere}${ambiguousDetail}${gs1Detail}`);
  };

  const setItemTrackingMode = async mode => {
    if (!selectedCatalog) return;
    const normalizedMode = mode === 'counted' ? 'counted' : 'unit';
    if (normalizedMode === selectedTrackingMode) return;

    setModeSaving(true);
    setError('');
    setMessage('');
    try {
      const records = makeInventoryTrackingModeChangeRecords(selectedCatalog, normalizedMode, data.expirations);
      const payload = await postRecords(records);
      const nextMode = inventoryTrackingMode(selectedCatalog, payload.expirations);
      const nextRecords = activeUnitRecords(payload.expirations, target.id, selectedCatalog.catalogId);
      const nextCount = countInventoryUnits(nextRecords);
      if (nextMode === 'counted') {
        setCountQuantity(nextCount > 0 ? String(nextCount) : '');
        setMessage(`${selectedCatalog.item} is now COUNTED / BULK everywhere. Existing saved units were consolidated automatically; enter the physical quantity for this bag and save the count.`);
      } else {
        setCountQuantity('');
        setMessage(`${selectedCatalog.item} is now INDIVIDUAL UNIT tracking. Any prior counted snapshots were moved to Archive; scan the physical units individually.`);
      }
    } catch (err) {
      setError(err?.message || 'Unable to change inventory tracking mode.');
    } finally {
      setModeSaving(false);
    }
  };

  const saveUnit = async andNext => {
    if (!target.id || !target.name) return setError('Choose the physical bag / container first.');
    if (!selectedCatalog) return setError('Scan or choose the inventory item first.');
    if (!form.noExpiration && !String(form.expirationDate || '').trim()) return setError('Enter this unit’s expiration date or mark it No expiration / N/A.');
    const normalizedExpiration = form.noExpiration ? '' : normalizeInventoryExpirationInput(form.expirationDate);
    if (!form.noExpiration && !normalizedExpiration) return setError('Enter expiration as MM/YYYY, MM/DD/YYYY, or YYYY-MM-DD.');

    setSaving(true);
    setError('');
    setMessage('');
    try {
      const record = makeInventoryUnitRecord(target, selectedCatalog, {
        barcode: currentBarcode,
        expirationDate: normalizedExpiration,
        noExpiration: form.noExpiration,
        lotSerial: form.lotSerial,
        notes: form.notes,
      });
      const payload = await postRecords([record]);
      const nextRecords = activeUnitRecords(payload.expirations, target.id, selectedCatalog.catalogId);
      const nextCount = countInventoryUnits(nextRecords);
      setMessage(`${selectedCatalog.item} saved as one physical unit in ${target.name}. Count: ${nextCount}/${selectedRequired || '—'}.`);
      if (andNext) clearSelection();
      else {
        setForm(current => ({ ...current, notes: '' }));
        setPrefilledSource(scanFormPrefill ? 'gs1' : 'previous');
      }
    } catch (err) {
      setError(err?.message || 'Unable to save inventory unit.');
    } finally {
      setSaving(false);
    }
  };

  const saveCount = async andNext => {
    if (!target.id || !target.name) return setError('Choose the physical bag / container first.');
    if (!selectedCatalog) return setError('Choose the counted inventory item first.');
    const quantity = Number(String(countQuantity || '').trim());
    if (!Number.isInteger(quantity) || quantity < 0) return setError('Enter the whole-number quantity physically present.');

    setSaving(true);
    setError('');
    setMessage('');
    try {
      const records = makeCountedInventoryRecords(target, selectedCatalog, selectedRecords, quantity, { notes: form.notes });
      const payload = await postRecords(records);
      const nextRecords = activeUnitRecords(payload.expirations, target.id, selectedCatalog.catalogId);
      const nextCount = countInventoryUnits(nextRecords);
      setMessage(`${selectedCatalog.item} counted inventory saved in ${target.name}: ${nextCount}/${selectedRequired || '—'}. Previous counts were moved to Archive.`);
      if (andNext) clearSelection();
      else {
        setCountQuantity(String(nextCount));
        setForm(current => ({ ...current, notes: '' }));
      }
    } catch (err) {
      setError(err?.message || 'Unable to save counted inventory.');
    } finally {
      setSaving(false);
    }
  };

  const removeUnit = async record => {
    if (!record?.recordId || !selectedCatalog) return;
    const confirmed = typeof window === 'undefined'
      ? true
      : window.confirm(`Remove this saved ${selectedCatalog.item} unit from ${target.name}? It will move to Archive and remain in history.`);
    if (!confirmed) return;

    setSaving(true);
    setError('');
    setMessage('');
    try {
      const existingNotes = String(record.notes || '').trim();
      const removalNote = 'Removed from active inventory in scanner';
      const updatedRecord = {
        ...record,
        returnedToLogistics: true,
        returnedDate: localInventoryDate(),
        notes: existingNotes ? `${existingNotes} | ${removalNote}` : removalNote,
        updatedAt: '',
      };
      const payload = await postRecords([updatedRecord]);
      const nextRecords = activeUnitRecords(payload.expirations, target.id, selectedCatalog.catalogId);
      const nextCount = countInventoryUnits(nextRecords);
      setMessage(`${selectedCatalog.item} unit removed from ${target.name} and moved to Archive. Count: ${nextCount}/${selectedRequired || '—'}.`);
    } catch (err) {
      setError(err?.message || 'Unable to remove inventory unit.');
    } finally {
      setSaving(false);
    }
  };

  const activeTargetValid = Boolean(target.id && target.name);

  return (
    <div className="inventory-root">
      <header className="inventory-topbar">
        <div>
          <h1>Line Medic Physical Inventory</h1>
          <p>Individual traceable units plus counted / bulk stock in each physical container</p>
        </div>
        <span>Private admin</span>
      </header>

      <main className="inventory-page inventory-scan-page inventory-unit-page">
        {error && <div className="inventory-error">{error}</div>}
        {message && <div className="inventory-scan-success">{message}</div>}

        <section className="inventory-card inventory-unit-ledger-note">
          <div>
            <strong>Live inventory ledger</strong>
            <span>Current inventory is mirrored to Current Stock. Consumed, retired, and superseded count records stay visible in Archive.</span>
          </div>
          <div className="inventory-unit-ledger-links">
            <a href={`${INVENTORY_SHEET_URL}#gid=${CURRENT_STOCK_GID}`} target="_blank" rel="noreferrer">Open Current Stock</a>
            <a href={`${INVENTORY_SHEET_URL}#gid=${ARCHIVE_GID}`} target="_blank" rel="noreferrer">Open Archive</a>
          </div>
        </section>

        <section className="inventory-card">
          <div className="inventory-section-head">
            <div><h2>1. Choose the physical container</h2><p>Inventory only the bag you physically have in front of you.</p></div>
          </div>
          <div className="inventory-scan-target-grid">
            <label>Known gear
              <select value={targets.some(item => item.id === target.id) ? target.id : ''} onChange={event => chooseTarget(event.target.value)}>
                <option value="">Custom physical container…</option>
                {targets.map(item => (
                  <option key={item.id} value={item.id}>{item.name} — {item.id}{item.seasonalStatus === 'oos' ? ' — OOS' : ''}</option>
                ))}
              </select>
            </label>
            <label>Container / bag ID
              <input value={targetId} onChange={event => setTargetId(event.target.value.toUpperCase())} placeholder="LM-PACK-01" />
            </label>
            <label>Container / bag name
              <input value={targetName} onChange={event => setTargetName(event.target.value)} placeholder="Line Medic Pack 01" />
            </label>
          </div>
          {targetMeta?.seasonalStatus === 'oos' && <div className="inventory-scan-warning">This container is marked seasonal OOS / not stocked. Do not inventory it unless you are intentionally putting it back in service.</div>}
        </section>

        {status === 'loading' && <section className="inventory-card">Loading inventory catalog…</section>}

        {status === 'ready' && activeTargetValid && (
          <>
            <section className={`inventory-card inventory-scan-readiness${reconciliation.summary.ready ? ' ready' : ''}`}>
              <div>
                <h2>{target.name}</h2>
                <p>{reconciliation.summary.complete} of {reconciliation.summary.total} applicable catalog items currently meet the saved inventory / expiration standard.</p>
              </div>
              <strong>{reconciliation.summary.ready ? 'READY' : 'BUILDING'}</strong>
            </section>

            <div className="inventory-summary inventory-scan-summary">
              <button type="button"><small>Short / missing</small><strong>{reconciliation.summary.short}</strong></button>
              <button type="button"><small>Expired</small><strong>{reconciliation.summary.expired}</strong></button>
              <button type="button"><small>Needs review</small><strong>{reconciliation.summary.needsReview}</strong></button>
              <button type="button"><small>Within {warningDays} days</small><strong>{reconciliation.summary.dueSoon}</strong></button>
            </div>

            {guidedTarget && (
              <InventoryBuildGuide
                targetId={target.id}
                catalog={scopedCatalog}
                reconciliation={reconciliation}
                expirations={data.expirations}
                selectedCatalogId={selectedCatalogId}
                onSelect={chooseCatalog}
              />
            )}

            <section className="inventory-card">
              <div className="inventory-section-head">
                <div><h2>{guidedTarget ? '3' : '2'}. Scan or find an item</h2><p>UPC/1D and GS1 scans use a shared barcode library. When GS1 supplies lot and expiration, those fields are prefilled for verification.</p></div>
                <button type="button" onClick={load}>Refresh inventory</button>
              </div>
              <InventoryBarcodeScanner onScan={handleBarcode} />
              {pendingBarcode && (
                <div className="inventory-scan-unmapped"><strong>Choose inventory slot: {pendingBarcode}</strong><span>Select the correct pouch/item below. Shared products can be mapped to more than one inventory slot.</span></div>
              )}
              <label className="inventory-scan-search">Find item manually
                <input value={search} onChange={event => setSearch(event.target.value)} placeholder="TXA, Adenosine, 4x4, IV Bag, trauma dressing…" />
              </label>
              {(search || pendingBarcode) && (
                <div className="inventory-scan-catalog-results">
                  {matchingCatalog.map(item => (
                    <button type="button" key={item.catalogId} onClick={() => chooseCatalog(item)} disabled={barcodeSaving}>
                      <span><strong>{item.item}</strong><small>{item.component || 'Other'} · {item.catalogId}</small></span>
                      <span className="inventory-unit-required">{barcodeSaving ? 'SAVING MAP…' : `${inventoryTrackingMode(item, data.expirations) === 'counted' ? 'COUNTED · ' : 'UNIT · '}Standard ${item.requiredQty || '—'}`}</span>
                    </button>
                  ))}
                  {!matchingCatalog.length && <div className="inventory-empty">No applicable catalog match for this bag.</div>}
                </div>
              )}
            </section>

            {selectedCatalog && (
              <section id="inventory-item-editor" className={`inventory-card inventory-scan-editor inventory-${selectedTrackingMode}`}>
                <div className="inventory-section-head">
                  <div>
                    <h2>{guidedTarget ? '4' : '3'}. {selectedCatalog.item} <span className="inventory-mode-badge">{selectedTrackingMode === 'counted' ? 'COUNTED / BULK' : 'INDIVIDUAL UNIT'}</span></h2>
                    <p>{selectedTrackingMode === 'counted'
                      ? <>Saved physical count: <strong>{selectedCount}</strong> / standard <strong>{selectedRequired || '—'}</strong>. Enter the total physically present once.</>
                      : <>Saved physical units: <strong>{selectedCount}</strong> / standard <strong>{selectedRequired || '—'}</strong>. This save always adds exactly <strong>1</strong>.</>}</p>
                  </div>
                  <button type="button" onClick={clearSelection}>Clear</button>
                </div>

                <label className="inventory-counted-toggle">
                  <input
                    type="checkbox"
                    checked={selectedTrackingMode === 'counted'}
                    disabled={saving || modeSaving}
                    onChange={event => setItemTrackingMode(event.target.checked ? 'counted' : 'unit')}
                  />
                  <span>
                    <strong>{modeSaving ? 'Saving tracking mode…' : 'Counted / bulk item'}</strong>
                    <small>Use this for supplies where we only care how many are in the bag. The choice is saved for every bag and future restocks.</small>
                  </span>
                </label>

                {selectedTrackingMode === 'counted' ? (
                  <>
                    <div className="inventory-counted-note">
                      <strong>Counted stock</strong>
                      <span>No barcode, lot, or expiration is required. Count what is physically in the bag and enter the total below.</span>
                    </div>
                    <div className="inventory-scan-editor-grid">
                      <label>Quantity physically present
                        <input value={countQuantity} onChange={event => setCountQuantity(event.target.value)} inputMode="numeric" placeholder={selectedRequired ? String(selectedRequired) : '0'} />
                      </label>
                      <label>Notes <span>optional</span>
                        <input value={form.notes} onChange={event => setForm(current => ({ ...current, notes: event.target.value }))} placeholder="optional count note" />
                      </label>
                    </div>
                    <div className="inventory-scan-actions">
                      <button type="button" onClick={() => saveCount(false)} disabled={saving || modeSaving}>{saving ? 'Saving…' : 'Save counted quantity'}</button>
                      <button type="button" onClick={() => saveCount(true)} disabled={saving || modeSaving}>{saving ? 'Saving…' : 'Save count & next item'}</button>
                    </div>
                  </>
                ) : (
                  <>
                    {currentBarcode && <div className="inventory-scan-info">Product barcode key: <strong>{currentBarcode}</strong></div>}
                    {currentScan?.isGs1 && (
                      <div className="inventory-scan-info">
                        <strong>GS1 decoded:</strong>{' '}
                        GTIN {currentScan.gtin || 'not supplied'} · Lot {currentScan.lot || currentScan.serial || 'not supplied'} · Expiration {formatInventoryExpirationInput(currentScan.expirationDate) || 'not supplied'}
                      </div>
                    )}
                    {prefilledSource === 'gs1' && <div className="inventory-unit-prefill">GS1 data prefilled the lot / serial and/or expiration fields that were actually supplied. Verify the physical package before saving.</div>}
                    {prefilledSource === 'previous' && <div className="inventory-unit-prefill">Previous unit’s lot / expiration was prefilled for speed. Verify the physical item before saving.</div>}

                    {selectedRecords.length > 0 && (
                      <div className="inventory-unit-existing">
                        <strong>Active physical units already saved</strong>
                        {selectedRecords.map((record, index) => (
                          <div key={record.recordId}>
                            <span>Unit {index + 1}</span>
                            <strong>{record.lotSerial ? `Lot ${record.lotSerial}` : 'No lot recorded'}</strong>
                            <small>{String(record.expirationState || '').toUpperCase() === 'NA' ? 'N/A expiration' : formatInventoryDate(record.expirationDate)}</small>
                            <button type="button" className="inventory-unit-remove" onClick={() => removeUnit(record)} disabled={saving || modeSaving}>Remove</button>
                          </div>
                        ))}
                      </div>
                    )}

                    <div className="inventory-scan-editor-grid">
                      <label>Unit quantity
                        <input value="1" disabled readOnly />
                      </label>
                      <label>Expiration date <span>type it in</span>
                        <input
                          type="text"
                          value={form.expirationDate}
                          disabled={form.noExpiration}
                          inputMode="numeric"
                          autoComplete="off"
                          placeholder="MM/YYYY or MM/DD/YYYY"
                          onChange={event => setForm(current => ({ ...current, expirationDate: event.target.value }))}
                        />
                        <small>Accepted: MM/YYYY, MM/DD/YYYY, or YYYY-MM-DD.</small>
                      </label>
                      <label>Lot / serial <span>recommended</span>
                        <input value={form.lotSerial} onChange={event => setForm(current => ({ ...current, lotSerial: event.target.value }))} />
                      </label>
                      <label>Notes <span>optional</span>
                        <input value={form.notes} onChange={event => setForm(current => ({ ...current, notes: event.target.value }))} />
                      </label>
                    </div>

                    <label className="inventory-check inventory-scan-na">
                      <input type="checkbox" checked={form.noExpiration} onChange={event => setForm(current => ({ ...current, noExpiration: event.target.checked, expirationDate: event.target.checked ? '' : current.expirationDate }))} />
                      No expiration / N/A
                    </label>

                    <div className="inventory-scan-actions">
                      <button type="button" onClick={() => saveUnit(false)} disabled={saving || modeSaving}>{saving ? 'Saving…' : 'Save this unit'}</button>
                      <button type="button" onClick={() => saveUnit(true)} disabled={saving || modeSaving}>{saving ? 'Saving…' : 'Save unit & scan next'}</button>
                    </div>
                  </>
                )}
              </section>
            )}

            <section className="inventory-card inventory-unit-review">
              <div className="inventory-section-head"><div><h2>Bag build check</h2><p>Shortages are based on the catalog standard versus the total active quantity saved, whether the item is individual or counted.</p></div></div>
              {reconciliation.rows.filter(row => row.blocker || row.dueSoon).slice(0, 30).map(row => (
                <div key={row.catalogId} className="inventory-unit-review-row">
                  <span><strong>{row.item}</strong><small>{row.component || 'Other'} · {row.catalogId} · {inventoryTrackingMode(row, data.expirations) === 'counted' ? 'Counted' : 'Individual'}</small></span>
                  <span><strong>{row.found}/{row.required}</strong><small>{row.expired ? 'Expired' : row.short ? 'Short' : row.needsReview ? 'Needs expiration review' : row.dueSoon ? 'Expiring soon' : 'OK'}</small></span>
                </div>
              ))}
              {!reconciliation.rows.some(row => row.blocker || row.dueSoon) && <div className="inventory-empty">No shortages or expiration issues in this bag.</div>}
            </section>
          </>
        )}
      </main>
    </div>
  );
}